import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { Button, Card, Chip, Icon, Logo, Pill } from "@b00k/ui";
import type { IconName } from "@b00k/ui";
import { TIER_LIST, tierPrice, tierRange } from "@b00k/domain";
import { useT } from "../i18n/index.js";
import { HeroCard } from "../components/HeroCard.js";
import { wartoscKonfiguracji } from "../lib/config.js";
import type { Lang } from "@b00k/domain";

/** Dziewięć kafli kategorii z prototypu — kolejność i kolory bez zmian. */
const CATEGORY_TILES: { icon: IconName; color: string; tint: string; titleKey: "cat1t"; descKey: "cat1d" }[] = [];

const TILES = [
  { icon: "bed", color: "#3E2712", tint: "rgba(62,39,18,.12)", t: "cat1t", d: "cat1d" },
  { icon: "car", color: "#13294B", tint: "rgba(19,41,75,.10)", t: "cat2t", d: "cat2d" },
  { icon: "ticket", color: "#FF5C00", tint: "rgba(255,92,0,.12)", t: "cat3t", d: "cat3d" },
  { icon: "scissors", color: "#8C1D40", tint: "rgba(140,29,64,.12)", t: "cat4t", d: "cat4d" },
  { icon: "sparkles", color: "#0F8A6E", tint: "rgba(23,201,160,.14)", t: "cat5t", d: "cat5d" },
  { icon: "med", color: "#4F8DFF", tint: "rgba(79,141,255,.14)", t: "cat6t", d: "cat6d" },
  { icon: "wrench", color: "#C8930A", tint: "rgba(200,147,10,.14)", t: "cat7t", d: "cat7d" },
  { icon: "book", color: "#0F8A6E", tint: "rgba(15,138,110,.12)", t: "cat8t", d: "cat8d" },
  { icon: "plus", color: "#6B6F76", tint: "rgba(107,111,118,.14)", t: "cat9t", d: "cat9d" },
] as const;

const STEPS = [
  { icon: "calendar", n: "01", t: "s1t", d: "s1d" },
  { icon: "chat", n: "02", t: "s2t", d: "s2d" },
  { icon: "lock", n: "03", t: "s3t", d: "s3d" },
] as const;

const FEATURES = [
  { icon: "calendar", key: "f1" },
  { icon: "lock", key: "f2" },
  { icon: "chat", key: "f3" },
  { icon: "user", key: "f4" },
] as const;

const OPERATOR_URL = wartoscKonfiguracji("operatorUrl", import.meta.env.VITE_OPERATOR_URL, "http://localhost:5274");
const CLIENT_URL = wartoscKonfiguracji("clientUrl", import.meta.env.VITE_CLIENT_URL, "http://localhost:5275");

export function LandingPage({ lang }: { lang: Lang }) {
  const t = useT();
  void CATEGORY_TILES;

  return (
    <>
      <section className="ld-hero">
        <div className="ld-hero__inner">
          <div className="ld-hero__copy">
            <Pill>{t.pill}</Pill>
            <h1>
              {t.title1}
              <br />
              {t.title2}
            </h1>
            <p>{t.sub}</p>
            <div className="ld-hero__cta">
              {/* Makieta nadpisuje akcent lokalnie zmiennymi na opakowaniu `display:contents`. */}
              <div style={{ "--accent": "#FF6A1A", "--on-accent": "#FFFFFF", display: "contents" } as CSSProperties}>
                <Link to="/rejestracja">
                  <Button arrow>{t.ctaBook}</Button>
                </Link>
              </div>
              <a href={CLIENT_URL}>
                <Button variant="ghost">{t.ctaOperator}</Button>
              </a>
            </div>
            <div className="ld-hero__meta">{t.heroMeta}</div>
          </div>
          <HeroCard lang={lang} />
        </div>
      </section>

      <section id="kategorie" style={{ background: "var(--surface-page)" }}>
        <div className="ld-inner">
          <div className="ld-kicker">{t.catsKick}</div>
          <h2 className="ld-h2" style={{ marginBottom: 12 }}>
            {t.catsTitle}
          </h2>
          <p className="ld-lead">{t.catsSub}</p>
          <div className="ld-grid-cats">
            {TILES.map((tile) => (
              <Card key={tile.t} hover>
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <span
                    className="ui-tile__icon"
                    style={{ background: tile.tint, color: tile.color }}
                  >
                    <Icon name={tile.icon} size={26} />
                  </span>
                  <h3 style={{ fontWeight: 700, fontSize: 19 }}>{t[tile.t]}</h3>
                  <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: "var(--text-muted)" }}>{t[tile.d]}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section id="jak" style={{ background: "var(--surface-alt)" }}>
        <div className="ld-inner">
          <div className="ld-kicker">{t.howKick}</div>
          <h2 className="ld-h2" style={{ marginBottom: 44 }}>
            {t.howTitle}
          </h2>
          <div className="ld-grid-3">
            {STEPS.map((s) => (
              <Card key={s.n}>
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <div className="ld-step__head">
                    <span className="ui-tile__icon" style={{ background: "var(--chip-bg)", color: "var(--chip-fg)" }}>
                      <Icon name={s.icon} />
                    </span>
                    <span className="ld-step__num">{s.n}</span>
                  </div>
                  <h3 style={{ fontWeight: 700, fontSize: 20 }}>{t[s.t]}</h3>
                  <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: "var(--text-muted)" }}>{t[s.d]}</p>
                </div>
              </Card>
            ))}
          </div>
          <div style={{ marginTop: 26, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <Chip variant="role">{t.newChip}</Chip>
            <span style={{ fontSize: 15, color: "var(--text-body)" }}>{t.chatLine}</span>
          </div>
        </div>
      </section>

      <section id="operatorzy" className="ld-ops" data-theme="dark">
        <div className="ld-ops__inner">
          <div style={{ display: "flex", flexDirection: "column", gap: 22, alignItems: "flex-start" }}>
            <div className="b00k-mono" style={{ fontSize: 12, letterSpacing: 3, color: "var(--nh-orange-vivid)" }}>
              {t.opsKick}
            </div>
            <h2 className="ld-h2">{t.opsTitle}</h2>
            <p style={{ margin: 0, maxWidth: 520, fontSize: 16, lineHeight: 1.65, color: "rgba(244,245,247,.75)" }}>
              {t.opsSub}
            </p>
            <a href={OPERATOR_URL}>
              <Button arrow>
                {t.opsCta}
              </Button>
            </a>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {FEATURES.map((f) => (
              <div className="ld-ops__feature" key={f.key}>
                <span>
                  <Icon name={f.icon} />
                </span>
                <span>{t[f.key]}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="cennik" style={{ background: "var(--surface-page)" }}>
        <div className="ld-inner">
          <div className="ld-kicker">{t.priceKick}</div>
          <h2 className="ld-h2" style={{ marginBottom: 44 }}>
            {t.priceTitle}
          </h2>
          <p className="ld-lead" style={{ marginTop: -28, marginBottom: 32 }}>{t.priceNote}</p>
          <div className="ld-grid-4">
            {TIER_LIST.map((tier) => {
              const cena = tierPrice(tier, lang);
              const cechy = [t[`t${TIER_LIST.indexOf(tier) + 1}f1` as keyof typeof t],
                             t[`t${TIER_LIST.indexOf(tier) + 1}f2` as keyof typeof t],
                             t[`t${TIER_LIST.indexOf(tier) + 1}f3` as keyof typeof t]];
              return (
                <Card key={tier.id} hover pad={tier.recommended ? "flush" : undefined}>
                  <div
                    className="ld-plan"
                    style={
                      tier.recommended
                        ? { borderTop: "4px solid var(--accent)", padding: "28px 30px", boxSizing: "border-box", height: "100%" }
                        : undefined
                    }
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span className="ld-plan__name" style={tier.recommended ? { color: "var(--accent)" } : undefined}>
                        {tier.id}
                      </span>
                      {tier.recommended && <Chip variant="tag">{t.recommended}</Chip>}
                    </div>
                    {/* Zakres operatorów jest tu jedyną informacją, która mówi, dla kogo
                        jest ten próg — bez niego cztery ceny wyglądają na dowolne. */}
                    <div className="ld-plan__ops">
                      {tierRange(tier)} {t.opsSuffix}
                    </div>
                    <div className="ld-plan__price">
                      {cena ? (
                        <>
                          {cena}
                          <small> /{t.month}</small>
                        </>
                      ) : (
                        t.custom
                      )}
                    </div>
                    <div className="ld-plan__features">
                      {cechy.map((c) => (
                        <span key={c}>{c}</span>
                      ))}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section id="kontakt" style={{ background: "var(--surface-alt)" }}>
        <div className="ld-inner ld-contact">
          <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
            <div className="ld-kicker" style={{ marginBottom: 0 }}>
              {t.conKick}
            </div>
            <h2 className="ld-h2">{t.conTitle}</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontWeight: 700, fontSize: 17 }}>Ośrodek Horyzont</span>
              <span className="b00k-mono b00k-muted" style={{ fontSize: 13 }}>
                UL. ZDROJOWA 12 · 58-580 SZKLARSKA PORĘBA
              </span>
            </div>
            <div className="b00k-mono" style={{ display: "flex", flexDirection: "column", gap: 12, fontSize: 14 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                <Icon name="phone" size={17} /> +48 75 000 00 00
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                <Icon name="mail" size={17} /> rezerwacje@b00k.online
              </span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}>
              <span className="b00k-mono b00k-muted" style={{ fontSize: 12, letterSpacing: 2 }}>
                {t.dirKick}
              </span>
              <div className="ld-dir">
                <span>01</span>
                <span>{t.dir1}</span>
              </div>
              <div className="ld-dir">
                <span>02</span>
                <span>{t.dir2}</span>
              </div>
              <div className="ld-dir">
                <span>03</span>
                <span>{t.dir3}</span>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <iframe
              title="Mapa dojazdu"
              src="https://www.openstreetmap.org/export/embed.html?bbox=15.49%2C50.81%2C15.55%2C50.85&layer=mapnik&marker=50.8286%2C15.5211"
              style={{
                // content-box: makieta liczy 440px treści, obramowanie dochodzi na zewnątrz.
                boxSizing: "content-box",
                width: "100%",
                height: 440,
                border: "1px solid var(--border-subtle)",
                borderRadius: "var(--radius-xl)",
                background: "var(--surface-card)",
              }}
            />
            <span className="b00k-mono b00k-muted" style={{ fontSize: 11, letterSpacing: 1 }}>
              50.8286 N · 15.5211 E · OPENSTREETMAP
            </span>
          </div>
        </div>
      </section>

      <footer className="ld-footer" data-theme="dark">
        <div className="ld-footer__inner">
          <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Logo size={22} color="var(--nh-orange-vivid)" />
            <span style={{ fontWeight: 800, fontSize: 17 }}>
              b<span style={{ color: "var(--nh-orange-vivid)" }}>00</span>k
            </span>
          </span>
          <span className="ld-footer__legal">B00K.ONLINE · PRODUKT NO-HUMAN PSA · KRS: 0000000000</span>
          <nav>
            <a href={CLIENT_URL}>{t.footUser}</a>
            <a href={OPERATOR_URL}>{t.footOp}</a>
          </nav>
        </div>
      </footer>
    </>
  );
}
